import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

type VerificationState = 'loading' | 'success' | 'expired' | 'invalid' | 'error';

export default function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<VerificationState>('loading');
  const [countdown, setCountdown] = useState(5);
  const { user } = useAuth();
  // Le client Supabase consomme le fragment d'URL à l'initialisation : on le capture au premier rendu.
  const [initialHash] = useState(() => window.location.hash);

  const verifyEmail = useCallback(async () => {
    try {
      const token = searchParams.get('token');
      const type = searchParams.get('type');

      if (!token || type !== 'signup') {
        // Lien {{ .ConfirmationURL }} : Supabase vérifie le lien puis redirige ici avec
        // la session (#access_token=…) ou une erreur (#error=…&error_code=…) dans le fragment.
        const hashParams = new URLSearchParams(initialHash.replace(/^#/, ''));

        if (hashParams.get('error')) {
          setState(hashParams.get('error_code') === 'otp_expired' ? 'expired' : 'invalid');
          return;
        }

        if (hashParams.get('access_token')) {
          // getSession() attend la fin de l'initialisation du client, donc le traitement du fragment.
          const { data: { session } } = await supabase.auth.getSession();
          setState(session?.user ? 'success' : 'invalid');
          return;
        }

        setState('invalid');
        return;
      }

      // Lien avec ?token=…&type=signup (modèle {{ .TokenHash }}) : vérifier le token avec Supabase
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'email'
      });

      if (error) {
        console.error('Erreur de vérification:', error);
        
        // Analyser le type d'erreur
        if (error.message?.includes('expired') || error.message?.includes('not found')) {
          setState('expired');
        } else {
          setState('invalid');
        }
        return;
      }

      if (data.user) {
        setState('success');
      } else {
        setState('invalid');
      }
    } catch (error) {
      console.error('Erreur de vérification:', error);
      setState('error');
    }
  }, [searchParams, initialHash]);

  useEffect(() => {
    verifyEmail();
  }, [verifyEmail]);

  useEffect(() => {
    if (state === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (state === 'success' && countdown === 0) {
      navigate('/dashboard');
    }
  }, [state, countdown, navigate]);

  const handleResendVerification = async () => {
    if (!user?.email) {
      navigate('/login');
      return;
    }

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`
        }
      });

      if (error) {
        console.error('Erreur renvoi email:', error);
      } else {
        alert('Email de vérification renvoyé ! Vérifiez votre boîte de réception.');
      }
    } catch (error) {
      console.error('Erreur renvoi email:', error);
    }
  };

  const renderContent = () => {
    switch (state) {
      case 'loading':
        return (
          <div className="text-center">
            <RefreshCw className="h-16 w-16 text-purple-500 mx-auto mb-6 animate-spin" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Vérification en cours...
            </h2>
            <p className="text-gray-300 text-lg">
              Nous vérifions votre email, veuillez patienter.
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Email vérifié avec succès !
            </h2>
            <p className="text-gray-300 text-lg mb-6">
              Votre compte a été activé. Vous allez être redirigé vers votre tableau de bord.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <p className="text-green-800 font-medium">
                Redirection automatique dans {countdown} seconde{countdown !== 1 ? 's' : ''}...
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
            >
              Accéder au tableau de bord maintenant
            </button>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Lien de vérification expiré
            </h2>
            <p className="text-gray-300 text-lg mb-6">
              Le lien de vérification que vous avez utilisé a expiré. 
              Ces liens ne sont valides que pendant 24 heures pour des raisons de sécurité.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <p className="text-yellow-800">
                <strong>Que faire maintenant ?</strong><br />
                Cliquez sur le bouton ci-dessous pour recevoir un nouveau lien de vérification.
              </p>
            </div>
            <div className="space-y-4">
              <button
                onClick={handleResendVerification}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
              >
                Renvoyer l'email de vérification
              </button>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-gray-600 text-white py-3 px-6 rounded-xl hover:bg-gray-700 transition-all duration-200"
              >
                Retour à la connexion
              </button>
            </div>
          </div>
        );

      case 'invalid':
        return (
          <div className="text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Lien de vérification invalide
            </h2>
            <p className="text-gray-300 text-lg mb-6">
              Le lien de vérification que vous avez utilisé n'est pas valide ou a déjà été utilisé.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-800">
                <strong>Causes possibles :</strong><br />
                • Le lien a déjà été utilisé<br />
                • Le lien est malformé<br />
                • Votre compte est déjà vérifié
              </p>
            </div>
            <div className="space-y-4">
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
              >
                Essayer de se connecter
              </button>
              <button
                onClick={handleResendVerification}
                className="w-full bg-gray-600 text-white py-3 px-6 rounded-xl hover:bg-gray-700 transition-all duration-200"
              >
                Demander un nouveau lien
              </button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Erreur de vérification
            </h2>
            <p className="text-gray-300 text-lg mb-6">
              Une erreur inattendue s'est produite lors de la vérification de votre email.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-800">
                Veuillez réessayer ou contacter le support si le problème persiste.
              </p>
            </div>
            <div className="space-y-4">
              <button
                onClick={verifyEmail}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
              >
                Réessayer la vérification
              </button>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-gray-600 text-white py-3 px-6 rounded-xl hover:bg-gray-700 transition-all duration-200"
              >
                Retour à la connexion
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-8 max-w-md mx-auto">
        {renderContent()}
      </div>
    </div>
  );
}
